import { getTimeZoneDate } from 'web-common/scripts/utils/DateHelper';
import FeatureToggleUtils from 'FeatureToggleUtils';
// Import only the side-effect-free helpers statically. The tracker entry point
// (@bizzabo/widget-analytics/dist/SnowplowTracker) injects sp.js at module-eval time,
// so it is imported lazily — after consent — inside initializeSnowplowTrackerIfEnabled.
import * as SchemasHelper from '@bizzabo/widget-analytics/dist/SnowplowTracker/SchemasHelper';
import {
    DISABLE_SNOWPLOW_TRACKING,
    ticketSchemaName,
    userSchemaName,
    sessionSchemaName,
    eventTypes
} from '@bizzabo/widget-analytics/dist/SnowplowTracker/constants';
import config from 'config';
import { ticketsRegisteredSessionIdsSelector } from '../selectors/AgendaSelectors';
import { createWorkerFromFunction } from 'web-common/scripts/utils/WebWorkerUtils';
import { ConsentCategory, onCategoryAllowed } from './consentHelper';
import {
    PROD_APP_ID,
    sessionCheck,
    TICKET_ENTITY_VALIDATION_LOG
} from '../constants/SnowplowConst';

// Loaded lazily once Performance (C0002) consent is granted; null until then, so
// every tracking call below no-ops pre-consent instead of loading sp.js.
let BizzaboSnowplowTracker = null;
let initStarted = false;
let pendingTrackerCalls = [];
const MAX_PENDING_TRACKER_CALLS = 50;

// The app sets Snowplow up at startup (ReactRouter calls
// initializeSnowplowTrackerIfEnabled and then immediately dispatches
// startSnowplowTrackingAction). With the old eager import the tracker already
// existed by then; behind the consent gate it does not, and OneTrust usually
// resolves only after the app has mounted — so these setup calls have to be
// held until the tracker arrives, however late that is. Dropping them makes
// Snowplow load on consent but never track.
//
// Queued in order and replayed once the tracker is ready, so consent gating
// changes *when* Snowplow loads rather than *whether* it tracks. Only setup
// calls (global context + start-tracking) are queued: one-off interaction and
// system events are still dropped when no tracker exists, since replaying those
// would report activity from before the visitor consented. If consent never
// comes the queue is simply never drained, so it is capped.
const withTracker = fn => {
    if (!isSnowplowTrackerEnabled()) {
        // Toggle off: initializeSnowplowTrackerIfEnabled never loads the tracker, so
        // queueing here would retain the captured entities for the whole visit.
        return;
    }
    if (BizzaboSnowplowTracker) {
        fn();
        return;
    }
    pendingTrackerCalls.push(fn);
    if (pendingTrackerCalls.length > MAX_PENDING_TRACKER_CALLS) {
        pendingTrackerCalls.shift();
    }
};

// The other half of that policy: one-off events are reported only if the tracker
// is already there, and are otherwise dropped rather than queued. Distinct from
// isSnowplowTrackerEnabled() below, which is the feature toggle and is also used
// for UI decisions outside this module.
const isTrackerReady = () => Boolean(BizzaboSnowplowTracker);

export const isSnowplowTrackerEnabled = () => !FeatureToggleUtils.isFeatureEnabled(DISABLE_SNOWPLOW_TRACKING); // can be moved to a fe-core itself
const isSnowplowInDevLikeInProd = () => FeatureToggleUtils.isFeatureEnabled('snowplowInDevLikeInProd');

export const initializeSnowplowTrackerIfEnabled = () => {
    if (!isSnowplowTrackerEnabled() || initStarted) {
        return;
    }
    initStarted = true;

    // Gate the Snowplow client (sp.js) behind Performance/C0002 consent. The dynamic
    // import defers the module's script-injecting side effect until the callback runs.
    onCategoryAllowed(ConsentCategory.Performance, async() => {
        // C0002 is granted here, so sp.js must load as an executable script. The page sets
        // window.__scriptTagTypeAttribute='block' to keep consent-gated scripts inert, and
        // widget-analytics stamps that onto sp.js; but OneTrust only un-blocks its own
        // 'text/plain' scripts (not 'block'), and a lazily-injected sp.js arrives after
        // OneTrust's scan — so it would stay inert forever. Neutralize the placeholder while
        // the tracker module injects sp.js; our gate is the consent authority here.
        const prevScriptType = window.__scriptTagTypeAttribute;
        window.__scriptTagTypeAttribute = undefined;
        let trackerModule;
        try {
            trackerModule = await import('@bizzabo/widget-analytics/dist/SnowplowTracker');
        } catch (error) {
            // The chunk failed to load (offline, CDN blip, ad blocker). Nothing will
            // ever drain the queue now, so release it instead of holding the captured
            // entities for the rest of the visit — and swallow the rejection rather
            // than leaving it unhandled. Analytics is best-effort; the page is fine.
            // Logged because the alternative is Snowplow going quiet with no trace.
            console.error('Failed to load SnowplowTracker; Snowplow will not track', error);
            pendingTrackerCalls = [];
            return;
        } finally {
            window.__scriptTagTypeAttribute = prevScriptType;
        }
        BizzaboSnowplowTracker = trackerModule.default;
        BizzaboSnowplowTracker.dedicatedInitTracker({
            env: config.env,
            snowplowInDevLikeInProd: isSnowplowInDevLikeInProd(),
            appId: trackerModule.snowplowAppId(PROD_APP_ID, config.env),
            platform: 'web',
            bufferSize: 1
        });
        window.BizzaboSnowplowTracker = BizzaboSnowplowTracker;

        const queued = pendingTrackerCalls;
        pendingTrackerCalls = [];
        queued.forEach(fn => fn());
    });
};

export const enableSnowplowTracking = () => withTracker(() => BizzaboSnowplowTracker.enableAutomaticTemporaryTracking());

export const addSPSessionToGlobalContext = (session, eventTimeZone) => {
    withTracker(() => {
        if (session.startDate) {
            session.startDateUTC = getTimeZoneDate(
                session.startDate, session.startMinute, eventTimeZone
            )?.format();
        }
        if (session.endDate) {
            session.endDateUTC = getTimeZoneDate(
                session.endDate, session.endMinute, eventTimeZone
            )?.format();
        }
        const sessionEntityData = [SchemasHelper.getSessionEntity(session)];
        // needs to prevent duplicating global context
        BizzaboSnowplowTracker.removeGlobalContext(sessionEntityData);
        BizzaboSnowplowTracker.setGlobalContext(sessionEntityData);
    });
};

export const removeGlobalSessionEntity = session => {
    withTracker(() => {
        if (!session) {
            return;
        }
        const sessionEntityData = [SchemasHelper.getSessionEntity(session)];
        BizzaboSnowplowTracker.removeGlobalContext(sessionEntityData);
    });
};

export const addSPAccountEventUserToGlobalContext = ( me, event ) => {
    withTracker(() => {
        const data = [SchemasHelper.getAccountEntity(event), SchemasHelper.getEventEntity(event)];
        if (me?.id) {
            data.push(SchemasHelper.getUserEntity(me));
        }
        BizzaboSnowplowTracker.clearGlobalContext();
        me?.primaryEmail && BizzaboSnowplowTracker.setGlobalUserId(me.primaryEmail);
        BizzaboSnowplowTracker.setGlobalContext(data);
    });
};


let timer = null;

export const stopSessionHeartBeat = () => {
    if (timer && isSnowplowTrackerEnabled()) {
        clearInterval(timer);
        timer = null;
    }
};

// Both call sites already check BizzaboSnowplowTracker, and it is never reset to
// null once loaded — the optional call is hardening for any future caller added
// outside those guards.
const sendSessionPingEntity = () => {
    const sessionPingEntity = SchemasHelper.getSessionEventEntity();
    BizzaboSnowplowTracker?.trackSelfDescribingEvent(sessionPingEntity);
};


export const startSessionHeartBeat = (delay) => {
    if (isTrackerReady()) {
        stopSessionHeartBeat();
        timer = setInterval(sendSessionPingEntity, delay);
    }
};

export const snowplowSessionCheckStatus = (status, pagehideEvent) => {
    if (isTrackerReady()) {
        if (status === sessionCheck.checkOut && !pagehideEvent) {
            sendSessionPingEntity();
        }
        const sessionAttendanceStatusEntity = SchemasHelper.getSessionAttendanceEntity(status);
        BizzaboSnowplowTracker.trackSelfDescribingEvent(sessionAttendanceStatusEntity);
        if (status === sessionCheck.checkIn) {
            sendSessionPingEntity();
        }
    }
};

export const addUserGlobalEntityStartTrack = me => {
    withTracker(() => {
        if (me?.id) {
            const data = [SchemasHelper.getUserEntity(me)];
            me?.primaryEmail && BizzaboSnowplowTracker.setGlobalUserId(me.primaryEmail);
            BizzaboSnowplowTracker.setGlobalContext(data);
        }
        enableSnowplowTracking();
    });
};

export const addTicketToGlobalContext = (tickets = [], shouldCheckForSession) => {
    withTracker(() => {
        if (tickets.length) {
            const virtualCheckinTickets = tickets.filter(({ virtualCheckin }) => !!virtualCheckin );
            if (virtualCheckinTickets.length) {
                const ticketEntity = [SchemasHelper.getTicketEntity(virtualCheckinTickets[0])];
                // needs to prevent duplicating global context
                BizzaboSnowplowTracker.removeGlobalContext(ticketEntity);
                BizzaboSnowplowTracker.setGlobalContext(ticketEntity);
            } else if (shouldCheckForSession) {
                sendSystemInfoEvent(TICKET_ENTITY_VALIDATION_LOG, 'addTicketToGlobalContext no ticket with virtualCheckin');
            }
        } else if (shouldCheckForSession) {
            sendSystemInfoEvent(TICKET_ENTITY_VALIDATION_LOG, 'addTicketToGlobalContext no tickets received');
        }
    });
};

export const startSPTrackUser = (shouldCheckForUser = false) => {
    withTracker(() => {
        BizzaboSnowplowTracker.checkFlagVsSchemaStartTrack(shouldCheckForUser, userSchemaName);
    });
};
export const startSPTicketTrack = shouldCheckForSession => {
    withTracker(() => {
        BizzaboSnowplowTracker.checkFlagVsSchemaStartTrack(shouldCheckForSession, sessionSchemaName);
    });
};

export const startSPSessionTrack = (shouldCheckForTicket = false) => {
    withTracker(() => {
        BizzaboSnowplowTracker.checkSchemaVsFlagStartTrack(shouldCheckForTicket, ticketSchemaName);
    });
};

export const sendSnowplowEventByType = (snowplowEvent) => {
    if (snowplowEvent?.eventType === eventTypes.BUTTON_CLICK && snowplowEvent.eventData) {
        const { buttonType, subCategory } = snowplowEvent.eventData;
        sendCustomClick(buttonType, subCategory);
    }
    if (snowplowEvent?.eventType === eventTypes.SYSTEM_INFO && snowplowEvent.eventData) {
        const { systemName, actionDescription } = snowplowEvent.eventData;
        sendSystemInfoEvent(systemName, actionDescription);
    }
};

const safeStringify = (data) => {
    try {
        return JSON.stringify(data);
    } catch (e) {
        return '';
    }
};

export const sendCustomClick = (buttonType, subCategory, refOrElem, description, action) => {
    if (!isTrackerReady()) {
        return;
    }

    const element =
        (refOrElem?.current?.getBoundingClientRect && refOrElem.current) ||
        (refOrElem?.getBoundingClientRect && refOrElem);
    const { top: positionY = null, left: positionX = null } = element?.getBoundingClientRect?.() || {};
    sendCustomClickByPosition(buttonType, subCategory, { positionX, positionY }, description, action);
};

export const sendCustomClickByPosition = (buttonType, subCategory, { positionX, positionY }, description, action) => {
    if (!isTrackerReady()) {
        return;
    }

    const clickEntity = SchemasHelper.getClickEventEntity({
        buttonType,
        subCategory,
        positionX,
        positionY,
        description: safeStringify(description),
        action: action,
    });
    BizzaboSnowplowTracker.trackSelfDescribingEvent(clickEntity);
};

export const sendSystemInfoEvent = (systemName, actionDescription) => {
    if (isTrackerReady() && systemName) {
        const systemEntity = SchemasHelper.getSystemInfoEventEntity({
            systemName,
            actionDescription
        });
        BizzaboSnowplowTracker.trackSelfDescribingEvent(systemEntity);
    }
};

export const trackCrossOriginTab = (tab, ms = 1000) => {
    const worker = createWorkerFromFunction(intervalWorker);
    return new Promise(resolve => {
        worker.addEventListener('message', event => {
            if (event.data === 'ping') {
                if (!tab || tab.closed) {
                    worker.terminate();
                    resolve(true);
                }
            }
        });
        worker.postMessage({ start: true, ms });
    });
};

const intervalWorker = () => {
    self.addEventListener('message', event => {
        if (event.data.start) {
            setInterval(() => {
                self.postMessage('ping');
            }, event.data.ms);
        }
    });
};

export const trackSystemEntityEvent = (systemName, actionDescription) => {
    if (isTrackerReady() && systemName) {
        const systemEntity = SchemasHelper.getSystemInfoEventEntity({
            systemName,
            actionDescription
        });
        BizzaboSnowplowTracker.sendSystemWithPageViewCheck(systemEntity);
    }
};
